<template>
  <div class="sidebar" :class="{ reduced: !isFromMobile && !viewFullMenu }">
    <button
      aria-hidden="true"
      class="toggle-menu"
      v-show="!isFromMobile"
      @click="toggleMenuSize"
    >
      <i class="material-icons clickable circle-toggle" v-show="viewFullMenu">
        arrow_left
      </i>
      <i class="material-icons clickable circle-toggle" v-show="!viewFullMenu">
        arrow_right
      </i>
    </button>
    <div class="h-100 sidebar-container">
      <header
        @click="handleItemClick(itemList.data.dashboard.link)"
        class="pt-0 logo-content clickable"
        v-if="!isFromMobile"
      >
        <img
          aria-hidden="true"
          src="/logos/icon.svg"
          alt="Logo TeamCulture"
          class="logo-reduced"
          v-show="!viewFullMenu"
        />
        <img
          aria-hidden="true"
          src="/logos/logo_inline.svg"
          alt="Logo TeamCulture"
          class="logo"
          v-show="viewFullMenu"
        />
      </header>

      <b-list-group class="sidebar-menu" id="sidebarMenu" v-list-group-active>
        <div v-if="isFromMobile" class="close-button">
          <img @click="toggleMenu" src="/icons/round-close.svg" alt="menu" />
        </div>

        <b-list-group-item
          id="homeItem"
          ref="homeItem"
          @click="handleItemClick(itemList.data.dashboard.link)"
          :class="{ active: itemList.data.dashboard.link.isActive }"
          data-list-role="item"
        >
          <div class="icon-span">
            <img
              class="new-icon"
              :title="menuResources.initial"
              :alt="menuResources.initial"
              :src="
                `/navigation/home${
                  itemList.data.dashboard.link.isActive ? '-active' : ''
                }.svg`
              "
            />
            <sidebar-item-label
              :type="itemList.type"
              :label="menuResources.initial"
              target="homeItem"
            />
          </div>
        </b-list-group-item>

        <can I="use" a="engagement">
          <can I="see" a="menu.reports" v-if="!getEngagementNewLayoutRule()">
            <b-list-group-item
              data-list-role="collapseParent"
              ref="reportItem"
              id="reportItem"
              @click="toggleSidebarItem('reportItemList')"
              :class="{
                active: itemList.data.report.link.isActive,
                'custom-collapsed': !itemList.data.report.link.isActive,
              }"
            >
              <div class="icon-span">
                <img
                  class="new-icon"
                  :title="menuResources.engagement"
                  :alt="menuResources.engagement"
                  :src="
                    `/navigation/engagement${
                      itemList.data.report.link.isActive ? '-active' : ''
                    }.svg`
                  "
                />
                <sidebar-item-label
                  :type="itemList.type"
                  :label="menuResources.engagement"
                  target="reportItem"
                />
              </div>
              <div class="wrap">
                <b-badge pill variant="danger" class="scale-4"></b-badge>
                <i
                  aria-hidden="true"
                  class="material-icons clickable when-opened"
                  v-show="viewFullMenu"
                >
                  expand_less
                </i>
                <i
                  aria-hidden="true"
                  class="material-icons clickable when-closed"
                  v-show="viewFullMenu"
                >
                  expand_more
                </i>
              </div>
            </b-list-group-item>
            <sidebar-item-list
              ref="reportItemList"
              :type="itemList.type"
              target="reportItem"
              :data="itemList.data.report.children"
            />
          </can>

          <b-list-group-item
            v-else
            id="engagementItem"
            @click="handleItemClick(itemList.data.report.link)"
            data-list-role="item"
          >
            <div class="icon-span">
              <img
                class="new-icon"
                :title="menuResources.engagement"
                :alt="menuResources.engagement"
                :src="`/navigation/engagement.svg`"
              />
              <sidebar-item-label
                :type="itemList.type"
                :label="menuResources.engagement"
                target="engagementItem"
              />
            </div>
          </b-list-group-item>
        </can>

        <can I="use" a="performance">
          <b-list-group-item
            id="performanceItem"
            v-if="!$can('access', 'read-only')"
            @click="handleItemClick(itemList.data.performance.link)"
            data-list-role="item"
          >
            <div class="icon-span">
              <img
                class="new-icon"
                :title="menuResources.performance"
                :alt="menuResources.performance"
                :src="`/navigation/performance.svg`"
              />
              <sidebar-item-label
                :type="itemList.type"
                :label="menuResources.performance"
                target="performanceItem"
              />
            </div>
          </b-list-group-item>
        </can>

        <can I="use" a="growth">
          <b-list-group-item
            id="growthItem"
            v-if="!$can('access', 'read-only')"
            @click="handleItemClick(itemList.data.growth.link)"
            data-list-role="item"
          >
            <div class="icon-span">
              <img
                class="new-icon"
                :title="menuResources.growth"
                :alt="menuResources.growth"
                :src="`/navigation/growth.svg`"
              />
              <sidebar-item-label
                :type="itemList.type"
                :label="menuResources.growth"
                target="growthItem"
              />
            </div>
          </b-list-group-item>
        </can>

        <b-list-group-item
          v-if="!$can('access', 'read-only') && !getNewLayoutRule('opinions')"
          @click="handleItemClick(itemList.data.feedback.link)"
          id="feedbackItem"
          :class="{ active: itemList.data.feedback.link.isActive }"
          data-list-role="item"
        >
          <div class="icon-span">
            <img
              class="new-icon"
              :title="menuResources.feedback"
              :alt="menuResources.feedback"
              :src="
                `/navigation/opinions${
                  itemList.data.feedback.link.isActive ? '-active' : ''
                }.svg`
              "
            />
            <sidebar-item-label
              :type="itemList.type"
              :label="menuResources.feedback"
              target="feedbackItem"
            />
          </div>
          <span
            class="ticket-bullet"
            v-if="unreadTickets && unreadTickets > 0"
          />
        </b-list-group-item>

        <b-list-group-item
          v-if="!$can('access', 'read-only')"
          id="customSurveysItem"
          ref="customSurveysItem"
          @click="handleItemClick(itemList.data.customSurveys.link)"
          :class="{ active: itemList.data.customSurveys.link.isActive }"
          data-list-role="item"
        >
          <div class="icon-span">
            <img
              class="new-icon"
              :title="menuResources.customSurveys"
              :alt="menuResources.customSurveys"
              :src="
                `/navigation/advanced-survery${
                  itemList.data.customSurveys.link.isActive ? '-active' : ''
                }.svg`
              "
            />
            <sidebar-item-label
              :type="itemList.type"
              :label="menuResources.customSurveys"
              target="customSurveysItem"
            />
          </div>
        </b-list-group-item>

        <!-- <can I="use" a="engagement">
          <b-list-group-item
            id="customPollItem"
            v-if="!$can('access', 'read-only')"
            @click="handleItemClick(itemList.data.customPoll.link)"
            :class="{ active: itemList.data.customPoll.link.isActive }"
            data-list-role="item"
          >
            <div class="icon-span">
              <img
                class="new-icon"
                :title="menuResources.customPoll"
                :alt="menuResources.customPoll"
                :src="
                  `/navigation/custom-survery${
                    itemList.data.customPoll.link.isActive ? '-active' : ''
                  }.svg`
                "
              />
              <sidebar-item-label
                :type="itemList.type"
                :label="menuResources.customPoll"
                target="customPollItem"
              />
            </div>
          </b-list-group-item>
        </can> -->

        <can I="see" a="menu.manager">
          <b-list-group-item
            id="configurationItem"
            @click="handleItemClick(itemList.data.settings.link)"
            :class="{ active: itemList.data.settings.link.isActive }"
            data-list-role="item"
          >
            <div class="icon-span">
              <img
                class="new-icon"
                :title="menuResources.settings"
                :alt="menuResources.settings"
                :src="
                  `/navigation/management${
                    itemList.data.settings.link.isActive ? '-active' : ''
                  }.svg`
                "
              />
              <sidebar-item-label
                :type="itemList.type"
                :label="menuResources.settings"
                target="configurationItem"
              />
            </div>
          </b-list-group-item>
        </can>
      </b-list-group>

      <footer class="user-options-footer">
        <div
          @click="toggleUserOptions('language')"
          class="footer d-flex clickable user-options"
        >
          <img
            :alt="menuResources.changeLanguage"
            aria-hidden="true"
            id="languageIcon"
            class="global-icon"
            src="/icons/shared/round-language.svg"
          />
          <div v-show="viewFullMenu" class="align-self-center p-0">
            <span class="middle-font text-capitalize">
              {{ menuResources.changeLanguage }}
            </span>
          </div>
          <div id="languageOptionsTooltip" class="options-tooltip">
            <b-tooltip
              container="languageOptionsTooltip"
              triggers="['click']"
              target="languageIcon"
              ref="languageTooltip"
            >
              <div class="tooltip-info language">
                <b-link
                  class="language-items"
                  v-for="language in languages"
                  :key="`language_${language.code}`"
                  @click="changeLanguage(language.code)"
                >
                  <img :src="language.icon" class="language-icon" />
                  <span :class="{ bold: languageSelected === language.code }">
                    {{ `${language.country} (${language.idiom})` }}
                  </span>
                </b-link>
              </div>
              <div v-show="!viewFullMenu" class="tooltip-box">
                <span class="middle-font text-capitalize">
                  {{ menuResources.changeLanguage }}
                </span>
              </div>
            </b-tooltip>
          </div>
        </div>
        <div
          @click="toggleUserOptions('user')"
          class="footer d-flex clickable user-options"
        >
          <i
            id="userIcon"
            class="material-icons clickable user-icon"
            aria-hidden="true"
          >
            account_circle
          </i>
          <div v-show="viewFullMenu" class="align-self-center p-0">
            <span class="medium-font text-capitalize">{{
              ` ${user.profile.firstname || ''} ${user.profile.lastname || ''}`
            }}</span>
            <span class="small-font text-lowercase d-flex">{{
              user.email
            }}</span>
          </div>
          <div id="userOptionsTooltip" class="options-tooltip">
            <b-tooltip
              container="userOptionsTooltip"
              triggers="['click']"
              target="userIcon"
              ref="userTooltip"
            >
              <div class="tooltip-info">
                <b-link
                  :to="{ path: '/users/profile' }"
                  class="d-block menu-option"
                >
                  <i class="material-icons clickable" aria-hidden="true">
                    settings
                  </i>
                  <span>{{ menuResources.personalSettings }}</span>
                </b-link>
                <b-link
                  :href="
                    `https://ajuda.teamculture.com.br/${
                      languageSelected.split('-')[0]
                    }`
                  "
                  target="_blank"
                  class="d-block menu-option help"
                >
                  <i class="material-icons clickable" aria-hidden="true">
                    help_outline
                  </i>
                  <span>{{ menuResources.help }}</span>
                </b-link>

                <b-link :to="{ path: '/logout' }" class="logout d-block">
                  <img
                    src="/icons/iconLogout.svg"
                    aria-hidden="true"
                    alt="logout"
                  />
                  <span>{{ menuResources.logout }}</span>
                </b-link>
              </div>
              <div v-show="!viewFullMenu" class="tooltip-box">
                <span class="medium-font text-capitalize">{{
                  ` ${user.profile.firstname || ''}
                    ${user.profile.lastname || ''}`
                }}</span>
                <span class="small-font text-lowercase d-flex">
                  {{ user.email }}
                </span>
              </div>
            </b-tooltip>
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  getUser,
  isToNewLayout,
  isEnterprisePlan,
  isEarlyAccessTrialAccount,
} from '@/service/user'
import SidebarItemList from './SidebarItemList.vue'
import SidebarItemLabel from './SidebarLabel.vue'

export default {
  props: ['isFromMobile', 'toggleMenu'],
  components: {
    SidebarItemList,
    SidebarItemLabel,
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      languages: 'languages',
      languageSelected: 'languageSelected',
      unreadTickets: 'tickets/unreadTickets',
    }),
    menuResources: function() {
      return this.resources.general.menu
    },
    user: function() {
      return getUser()
    },
    itemList: function() {
      const type = this.viewFullMenu
        ? this.TYPE_ITEM_NORMAL
        : this.TYPE_ITEM_TOOLTIP
      const report = this.getReportItemList()
      const settings = this.getSettingsItemList()

      const data = {
        report,
        settings,
        performance: {
          link: {
            name: 'performance',
            isActive: false,
          },
        },
        growth: {
          link: {
            name: 'growth',
            isActive: false,
          },
        },
        dashboard: {
          link: {
            name: 'dashboard',
            isActive: ['dashboard'].includes(this.$route.name),
          },
        },
        feedback: {
          link: {
            name: 'feedbacks.tickets',
            isActive: ['feedbacks.tickets', 'feedbacks.tickets.open'].includes(
              this.$route.name
            ),
          },
        },
        customSurveys: {
          link: {
            name: 'custom-surveys',
            isActive: [
              'custom-surveys',
              'custom-surveys.create',
              'custom-surveys.feedbacks',
              'custom-surveys.participation',
              'custom-surveys.reports',
              'custom-surveys.settings',
            ].includes(this.$route.name),
          },
        },
        // customPoll: {
        //   link: {
        //     name: 'customPoll',
        //     isActive: [
        //       'customPoll',
        //       'customPoll.report',
        //       'customPoll.edit',
        //       'customPoll.new',
        //     ].includes(this.$route.name),
        //   },
        // },
      }

      return {
        type,
        data,
      }
    },
    tooltipCustomClasses() {
      return this.viewFullMenu
        ? ['white-itens', 'tooltip-sidebar-menu']
        : ['light-itens', 'tooltip-sidebar-menu']
    },
  },
  data() {
    return {
      viewFullMenu: true,
      TYPE_ITEM_NORMAL: 'normal',
      TYPE_ITEM_TOOLTIP: 'tooltip',
    }
  },
  watch: {
    viewFullMenu: function(value) {
      this.$root.$emit('menu:collapse', value)
    },
  },
  methods: {
    getNewLayoutRule(context) {
      const canAccessNewLayout =
        (isEnterprisePlan() || isEarlyAccessTrialAccount()) &&
        !this.$can('access', 'read-only')

      return canAccessNewLayout && isToNewLayout(context)
    },
    getEngagementNewLayoutRule() {
      return [
        'climate',
        'culture',
        'participation',
        'groups-comparison',
        'benchmark',
        'action-plans',
      ].some(layout => this.getNewLayoutRule(layout))
    },

    changeLanguage(language) {
      this.$store.dispatch('setLanguage', language)
      this.$store.dispatch('users/editUserLanguage', language)
    },
    toggleMenuSize() {
      if (!this.isFromMobile) {
        this.viewFullMenu = !this.viewFullMenu
      }
      this.$root.$emit('listGroupActive::hide::collapse')
      this.viewFullMenu && this.$root.$emit('bv::tooltip::hidden')
    },
    toggleSidebarItem(id) {
      this.$refs[id].toggleOpen()
    },
    toggleUserOptions(type) {
      !this.$refs[`${type}Tooltip`].localShow
        ? this.$refs[`${type}Tooltip`].$emit('open')
        : this.$refs[`${type}Tooltip`].$emit('close')
      this.$refs[`${type}Tooltip`].localShow &&
        this.addCustomClass(
          document.querySelector(`#${type}OptionsTooltip .tooltip`)
        )
    },
    addCustomClass(el) {
      if (!el) return
      const tooltip = el.closest('.tooltip')
      this.tooltipCustomClasses.forEach(customClass =>
        tooltip.classList.add(customClass)
      )
    },
    handleItemClick(router) {
      this.$router.push(router)
    },
    getSettingsItemList() {
      let routes = []

      const canAccessPageUsers = this.$can('access', 'page.users')
      const canSeePageGroups = this.$can('see', 'menu.groups')
      const canSeeMenuAdmin = this.$can('see', 'menu.admin')

      const settings = {
        link: {
          name: '',
          isActive: false,
        },
        children: [],
      }

      if (canAccessPageUsers) {
        settings.link.name = 'dashboard.users'
      } else if (canSeePageGroups) {
        settings.link.name = 'dashboard.groups'
      } else if (canSeeMenuAdmin) {
        settings.link.name = 'dashboard.user-attributes'
      } else {
        settings.link.name = 'dashboard'
      }

      if (canAccessPageUsers) {
        const userRoutes = [
          'dashboard.users',
          'dashboard.users.send.invite',
          'dashboard.users.edit',
        ]
        settings.children.push({
          label: this.menuResources.adminUsers,
          link: {
            name: 'dashboard.users',
            isActive: userRoutes.includes(this.$route.name),
          },
        })
        routes.push(...userRoutes)
      }

      if (canSeePageGroups) {
        const groupRoutes = ['dashboard.groups']
        settings.children.push({
          label: this.menuResources.adminGroups,
          link: {
            name: 'dashboard.groups',
            isActive: groupRoutes.includes(this.$route.name),
          },
        })
        routes.push(...groupRoutes)
      }

      if (canSeeMenuAdmin) {
        // user attributes route
        const userAttributesRoutes = [
          'user-attributes',
          'user-attributes.detail',
        ]
        settings.children.push({
          label: this.menuResources.userAttributes,
          link: {
            name: 'user-attributes',
            isActive: userAttributesRoutes.includes(this.$route.name),
          },
        })

        // integration route
        const integrationRoutes = ['integrations']
        settings.children.push({
          label: this.menuResources.integrations,
          link: {
            name: 'integrations',
            isActive: integrationRoutes.includes(this.$route.name),
          },
        })

        // payment routes
        const paymentRoutes = [
          'account.details',
          'account.licenses',
          'account.plans',
          'account.charging',
        ]
        settings.children.push({
          label: this.menuResources.account,
          link: {
            name: 'account.details',
            isActive: paymentRoutes.includes(this.$route.name),
          },
        })

        // audit logs route
        const auditLogsRoutes = ['audit-logs']
        settings.children.push({
          label: this.menuResources.auditLogs,
          link: {
            name: 'audit-logs',
            isActive: auditLogsRoutes.includes(this.$route.name),
          },
        })

        // all routes
        const all = [
          ...userAttributesRoutes,
          ...integrationRoutes,
          ...paymentRoutes,
          ...auditLogsRoutes,
        ]
        routes.push(...all)
      }

      settings.link.isActive = routes.includes(this.$route.name)
      return settings
    },
    getReportItemList() {
      return {
        link: {
          name: 'reports',
          isActive: [
            'reports',
            'culture',
            'participation',
            'comparation',
            'company.metric',
            'benchmark',
            'action.plan',
            'analytics',
          ].includes(this.$route.name),
        },
        children: [
          {
            label: this.menuResources.climate,
            link: {
              name: 'reports',
              isActive: ['reports', 'company.metric'].includes(
                this.$route.name
              ),
              params: { groupId: 'company' },
            },
          },
          {
            label: this.menuResources.culture,
            link: {
              name: 'culture',
              isActive: ['culture'].includes(this.$route.name),
              params: { groupId: 'company' },
            },
          },
          {
            label: this.menuResources.participation,
            link: {
              name: 'participation',
              isActive: ['participation'].includes(this.$route.name),
              params: { groupId: 'company' },
            },
          },
          {
            label: this.menuResources.comparation,
            link: {
              name: 'comparation',
              isActive: ['comparation'].includes(this.$route.name),
            },
          },
          {
            label: 'Benchmark',
            link: {
              name: 'benchmark',
              isActive: ['benchmark'].includes(this.$route.name),
              params: { groupId: 'company' },
            },
          },
          {
            label: this.menuResources.actionPlan,
            link: {
              name: 'action.plan',
              isActive: ['action.plan'].includes(this.$route.name),
            },
          },
          {
            label: this.menuResources.analytics,
            link: {
              name: 'analytics',
              isActive: ['analytics'].includes(this.$route.name),
            },
          },
        ],
      }
    },
  },
}
</script>

<style lang="scss">
@import '~@/styles/custom/colors';

.sidebar-menu .tooltip-sidebar-menu .tooltip-inner,
.options-tooltip .tooltip-sidebar-menu .tooltip-inner {
  border-radius: 8px;
  border: 0.5px solid map-get($theme-colors, 'light03');
  border-left: inherit;
  padding: 0;
  text-align: left;
  max-width: none;
  width: 262px;
  margin-left: 10px;

  .tooltip-info {
    padding: 20px 16px;

    &.language {
      padding: 8px 16px;
    }

    .menu-option {
      padding-bottom: 10px;
      span {
        margin-left: 28px;
      }
      i {
        font-size: 12pt;
        color: map-get($theme-colors, 'primary');
      }
    }

    .help {
      border-bottom: 1px solid map-get($theme-colors, 'gray01');
    }

    .logout {
      padding-top: 10px;
    }
  }

  .list-group {
    padding: 20px 30px;
  }

  .tooltip-box {
    color: map-get($theme-colors, 'primary');
    background-color: map-get($theme-colors, 'light');
    padding: 10px 30px 20px;
  }

  .list-group-item {
    background: map-get($theme-colors, 'white') !important;
    border: none !important;
    font-size: 14px !important;
    height: initial !important;
    justify-content: flex-start !important;
    padding: 0.75rem 0 !important;
    width: 100% !important;

    &:first-of-type {
      padding-top: 0 !important;
    }

    &:last-of-type {
      padding-bottom: 0 !important;
    }

    &:hover,
    &.active {
      background-color: inherit !important;
    }
  }

  a {
    color: map-get($theme-colors, 'primary');

    &:hover,
    &.active {
      color: map-get($theme-colors, 'red');
    }
  }

  img {
    margin-right: 20px;
    vertical-align: middle;
  }
}
</style>

<style scoped lang="scss">
@import '~@/styles/custom/colors';
@import '~bootstrap/scss/_functions';
@import '~bootstrap/scss/_variables';
@import '~bootstrap/scss/mixins/breakpoints';

$marginRightIcons: 0.8125rem;
$sidebarWidthSmUp: 360px;
$sidebarWidth: 90vw;
$sidebarWidthReduced: 84px;
$footerPaddingLeft: 20px;
$footerPaddingLeftReduced: 6px;
$footerHeight: 40px;
$listGroupItemHeight: 55px;

@keyframes ticketEffectIn {
  0% {
    border-top-width: 1px;
    border-left-width: 1px;
    border-right-width: 1px;
    border-bottom-width: 1px;
    box-shadow: 0 0 0 0 map-get($theme-colors, 'red05');
  }

  70% {
    border-top-width: 1px;
    border-left-width: 1px;
    border-right-width: 1px;
    border-bottom-width: 1px;
    box-shadow: 0 0 0 3px map-get($theme-colors, 'red05');
  }

  100% {
    border-top-width: 1px;
    border-left-width: 1px;
    border-right-width: 1px;
    border-bottom-width: 1px;
    box-shadow: 0 0 0 0 map-get($theme-colors, 'red05');
  }
}

@keyframes circleEffectIn {
  0% {
    font-weight: 400;
    box-shadow: 0 0 0 0 map-get($theme-colors, 'red04');
  }
  70% {
    font-weight: 900;
    box-shadow: 0 0 30px 0 map-get($theme-colors, 'red04');
  }
  100% {
    font-weight: 400;
    box-shadow: 0 0 1000px 0 map-get($theme-colors, 'red04');
  }
}

.icon-span {
  span {
    text-align: left;
  }

  .material-icons {
    height: 16.43px;
    font-size: 18px;
    margin-right: $marginRightIcons;
    width: 18px;

    &.rotate45 {
      transform: rotate(45deg);
    }
  }

  .new-icon {
    margin-right: $marginRightIcons;
    width: 3rem;
  }
}

.sidebar {
  background-color: map-get($theme-colors, 'white');
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0.625rem;
  transition: all ease-in-out 0.1s;
  width: $sidebarWidth;
  z-index: 1;

  @include media-breakpoint-up(sm) {
    width: $sidebarWidthSmUp;
  }

  &.reduced {
    transition: all ease-in-out 0.1s;
    width: $sidebarWidthReduced;

    .logo-content {
      text-align: center;
    }

    .sub-list {
      font-size: 0.7rem;
      overflow: hidden !important;
      padding-left: 10px !important;
      text-overflow: ellipsis;
    }
    .wrap {
      left: -8px;
      position: relative;
    }

    /deep/ .list-group-item {
      height: $listGroupItemHeight;
      justify-content: center;
      margin: 1px auto;
      padding: 0;
      width: 55px;

      .icon-span {
        justify-content: center;
      }

      .material-icons,
      .new-icon {
        margin-right: 0;
      }

      .material-icons {
        &.rotate45 {
          transform: rotate(45deg);
        }
      }

      .ticket-bullet {
        margin-right: -9px;
        margin-bottom: 19px;
      }
    }

    .footer {
      padding-left: $footerPaddingLeftReduced;

      @include media-breakpoint-up(sm) {
        width: calc(#{$sidebarWidthReduced} - #{$footerPaddingLeftReduced});
      }
    }

    .sidebar-menu {
      overflow: initial !important;
    }

    &:hover .circle-toggle {
      animation: circleEffectIn 1s;
      border-radius: 7px;
    }
  }

  .sidebar-container {
    display: flex;
    flex-direction: column;

    .sidebar-menu {
      display: block;
      text-align: center;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 0 0.375rem;
      align-self: center;
    }
  }

  .medium-font {
    font-size: 14px;
  }

  .middle-font {
    font-size: 12px;
  }

  .small-font {
    font-size: 10px;
  }

  .user-icon {
    font-size: 40px;
  }

  .global-icon {
    width: 40px;
    height: 1.75rem;
  }

  .language-icon {
    width: 26px;
    height: 26px;
  }

  .language-items {
    display: block;
    padding: 9px 0px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid map-get($theme-colors, 'gray01');

    &:last-child {
      border-bottom: unset;
    }
  }

  .circle-toggle {
    margin-top: -3.5px;
    margin-left: 2px;
  }

  .ticket-bullet {
    background-color: map-get($theme-colors, 'secondary');
    border-radius: 5px;
    height: 8px;
    margin-right: 4.6px;
    width: 8px;
    border: 1px solid map-get($theme-colors, 'red05');
    animation: ticketEffectIn 1s infinite;
  }

  .logo-content {
    padding-bottom: 1.5625rem;

    .logo {
      height: 1.875rem;
      width: 100%;

      &-reduced {
        width: 60px;
      }
    }
  }

  .toggle-menu {
    background-color: map-get($theme-colors, 'white');
    border: none;
    border-radius: 25px;
    color: map-get($theme-colors, 'secondary');
    height: 18px !important;
    padding: 0;
    position: absolute;
    right: -7px;
    top: 15px;
    width: 18px;
  }

  .footer {
    background-color: map-get($theme-colors, 'white');
    color: map-get($theme-colors, 'primary');
    justify-content: flex-start;
    max-width: calc(#{$sidebarWidth} - #{$footerPaddingLeft});
    min-height: calc(#{$footerHeight} + 5px);
    width: 100%;
    margin-top: 0.75rem;
    align-items: center;

    @include media-breakpoint-up(sm) {
      width: calc(#{$sidebarWidthSmUp} - #{$footerPaddingLeft});
    }

    .btn-group > .btn:first-child {
      padding: 0px;
    }
  }

  .backdrop {
    height: 100%;
  }

  .list-group {
    flex-basis: 100%;
  }

  /deep/ .list-group-item {
    align-items: center;
    background: map-get($theme-colors, 'white');
    border: none;
    border-radius: 30px;
    color: map-get($theme-colors, 'primary');
    cursor: pointer;
    display: inline-flex;
    font-size: 14px;
    line-height: 24px;
    height: $listGroupItemHeight;
    justify-content: space-between;
    margin: 1px 0px;
    padding: 0;
    outline: none;
    text-decoration: none;
    width: 100%;

    &:hover,
    &.active {
      z-index: 0;
    }

    &.active .icon-span {
      font-weight: 600;
    }

    &.disabled {
      cursor: blocked;
      color: map-get($theme-colors, 'gray03');
    }

    div {
      align-items: center;
      display: inline-flex;
    }
  }

  .collapse,
  .collapsing {
    /deep/ .list-group-item {
      justify-content: flex-start;
      padding: 12px 28px 12px 20px;

      &:hover,
      &.active {
        color: map-get($theme-colors, 'red');
        background: none;
        border-radius: 0px;
      }
    }
  }

  .custom-collapsed > .wrap > .when-opened,
  :not(.custom-collapsed) > .wrap > .when-closed {
    display: none;
  }

  .submenu /deep/ .list-group-item {
    padding-left: 60px;
    font-size: 12px;
    text-transform: initial;
  }
}

.close-button {
  font-size: 30px;
  margin-bottom: 20px;
  text-align: left;

  img {
    background-color: map-get($theme-colors, 'white');
    border-radius: 25px;
    height: 40px;
    text-align: center;
    padding: 5px;
    width: 40px;

    &:active {
      background-color: darken(map-get($theme-colors, 'white'), 10%);
    }
  }
}

.user-options-footer {
  padding: 0 0.375rem;
  margin-bottom: 1.5rem;
  border-top: 1px solid map-get($theme-colors, 'white');
}

.user-options {
  margin-top: 10px !important;
  border-radius: 30px;
  width: inherit !important;
  &:hover {
    background: map-get($theme-colors, 'white');
    z-index: 0;
  }
}

#homeItem .icon-span span {
  color: #fccb1c;
}

#engagementItem,
#reportItem,
#feedbackItem {
  .icon-span span {
    color: #e42e62;
  }
}

#performanceItem .icon-span span {
  color: #6390f9;
}

#growthItem .icon-span span {
  color: #26da9e;
}

#customSurveysItem,
#customPollItem {
  .icon-span span {
    color: #be63f9;
  }
}

#configurationItem .icon-span span {
  color: #7a7a7a;
}
</style>
